import { BaseRepository } from '@/base/base-repository';

export interface ContestLeaderboardEntry {
	rank: number;
	address: string;
	displayName?: string;
	avatarUrl?: string;
	wagerUsd: number;
	prizeUsd?: number;
	prizePercentage?: number;
	isYou?: boolean;
}

export interface ContestUserProgress {
	rank?: number;
	wagerUsd: number;
	targetRank?: number;
	amountNeededUsd?: number;
	targetLabel?: string;
}

export interface ContestChampion extends ContestLeaderboardEntry {
	weekRangeLabel: string;
}

export interface ContestPrizeDistributionEntry {
	rank: number;
	percentage: number;
	label: string;
}

export interface WeeklyContestSnapshot {
	id: string;
	title: string;
	prizePoolUsd: number;
	prizeCurrency: string;
	startAt: string;
	endAt: string;
	updatedAt: string;
	leaderboard: ContestLeaderboardEntry[];
	lastChampion?: ContestChampion;
	user: ContestUserProgress;
	supportedCoins: string[];
	prizeDistribution: ContestPrizeDistributionEntry[];
}

export interface WeeklyContestHistoryEntry {
	id: string;
	rangeLabel: string;
	prizePoolUsd: number;
	leaderboard: ContestLeaderboardEntry[];
}

export interface WeeklyContestData {
	snapshot: WeeklyContestSnapshot;
	history: WeeklyContestHistoryEntry[];
}

interface WeeklyContestResponse {
	contest?: Partial<WeeklyContestSnapshot>;
	history?: Array<Partial<WeeklyContestHistoryEntry>>;
}

class ContestRepository extends BaseRepository {
	async getWeeklyContest(signal?: AbortSignal): Promise<WeeklyContestData> {
		try {
			const response = await this.httpClient<WeeklyContestResponse>({
				method: 'get',
				url: '/contests/weekly',
				signal,
				raw: true,
			});

			return normalizeWeeklyContest(response);
		} catch (error) {
			console.warn('[contestRepository] Falling back to sample weekly race data', error);
			return buildFallbackWeeklyContest();
		}
	}
}

function normalizeWeeklyContest(payload?: WeeklyContestResponse): WeeklyContestData {
	if (!payload?.contest) {
		return buildFallbackWeeklyContest();
	}

	const snapshot = payload.contest;
	const leaderboard = Array.isArray(snapshot.leaderboard)
		? snapshot.leaderboard.map((entry, index) => ({
				...entry,
				rank: entry.rank ?? index + 1,
				wagerUsd: Number(entry.wagerUsd ?? 0),
				prizeUsd: entry.prizeUsd !== undefined ? Number(entry.prizeUsd) : undefined,
				prizePercentage: entry.prizePercentage !== undefined ? Number(entry.prizePercentage) : undefined,
			}))
		: [];

	const history = Array.isArray(payload.history)
		? payload.history.map((item, historyIndex) => ({
				id: item.id ?? `history-${historyIndex}`,
				rangeLabel: item.rangeLabel ?? 'Previous Week',
				prizePoolUsd: Number(item.prizePoolUsd ?? 0),
				leaderboard: Array.isArray(item.leaderboard)
					? item.leaderboard.map((entry, index) => ({
							...entry,
							rank: entry.rank ?? index + 1,
							wagerUsd: Number(entry.wagerUsd ?? 0),
							prizeUsd: entry.prizeUsd !== undefined ? Number(entry.prizeUsd) : undefined,
							prizePercentage: entry.prizePercentage !== undefined ? Number(entry.prizePercentage) : undefined,
						}))
					: [],
			}))
		: [];

	const now = new Date();
	const startAt = snapshot.startAt ?? new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000).toISOString();
	const endAt = snapshot.endAt ?? new Date(now.getTime() + 4 * 24 * 60 * 60 * 1000).toISOString();

	return {
		snapshot: {
			id: snapshot.id ?? 'weekly-contest',
			title: snapshot.title ?? 'Weekly Wagering Race',
			prizePoolUsd: Number(snapshot.prizePoolUsd ?? 0),
			prizeCurrency: snapshot.prizeCurrency ?? 'USD',
			startAt,
			endAt,
			updatedAt: snapshot.updatedAt ?? new Date().toISOString(),
			leaderboard,
			lastChampion: snapshot.lastChampion
				? {
						...snapshot.lastChampion,
						weekRangeLabel: snapshot.lastChampion.weekRangeLabel ?? 'Last Week',
						wagerUsd: Number(snapshot.lastChampion.wagerUsd ?? 0),
						prizeUsd: snapshot.lastChampion.prizeUsd !== undefined ? Number(snapshot.lastChampion.prizeUsd) : undefined,
						prizePercentage: snapshot.lastChampion.prizePercentage !== undefined ? Number(snapshot.lastChampion.prizePercentage) : undefined,
					}
				: undefined,
			user: {
				rank: snapshot.user?.rank,
				wagerUsd: Number(snapshot.user?.wagerUsd ?? 0),
				targetRank: snapshot.user?.targetRank,
				amountNeededUsd: snapshot.user?.amountNeededUsd !== undefined ? Number(snapshot.user.amountNeededUsd) : undefined,
				targetLabel: snapshot.user?.targetLabel ?? 'Top 10',
			},
			supportedCoins: snapshot.supportedCoins ?? [],
			prizeDistribution: Array.isArray(snapshot.prizeDistribution)
				? snapshot.prizeDistribution.map((item, index) => ({
						...item,
						rank: item.rank ?? index + 1,
						percentage: Number(item.percentage ?? 0),
						label: item.label ?? `Rank ${item.rank ?? index + 1}`,
					}))
				: [],
		},
		history,
	};
}

function buildFallbackWeeklyContest(): WeeklyContestData {
	const now = new Date();
	const startOfWeek = getStartOfWeek(now);
	const endOfWeek = new Date(startOfWeek.getTime() + 7 * 24 * 60 * 60 * 1000);

	const prizePool = 150_000 + Math.random() * 45_000;

	const leaderboard: ContestLeaderboardEntry[] = [
		{
			rank: 1,
			address: '0x8b12...f9d3',
			displayName: 'foxglove',
			wagerUsd: 48231.12,
			prizePercentage: 50,
			prizeUsd: prizePool * 0.5,
		},
		{
			rank: 2,
			address: '0x4f90...71ac',
			displayName: 'arclight',
			wagerUsd: 40320.88,
			prizePercentage: 25,
			prizeUsd: prizePool * 0.25,
		},
		{
			rank: 3,
			address: '0x9c20...7051',
			displayName: 'sapphire',
			wagerUsd: 36542.17,
			prizePercentage: 12,
			prizeUsd: prizePool * 0.12,
		},
		{
			rank: 4,
			address: '0x2ea1...12ef',
			displayName: 'monsoon',
			wagerUsd: 26411.0,
			prizePercentage: 6,
			prizeUsd: prizePool * 0.06,
		},
		{
			rank: 5,
			address: '0x3abf...b01d',
			displayName: 'tidalwave',
			wagerUsd: 23150.75,
			prizePercentage: 3,
			prizeUsd: prizePool * 0.03,
		},
		{
			rank: 6,
			address: '0x5f31...b540',
			displayName: 'nightjar',
			wagerUsd: 19121.34,
			prizePercentage: 1.5,
			prizeUsd: prizePool * 0.015,
		},
		{
			rank: 7,
			address: '0x7aa9...3d2c',
			displayName: 'ember',
			wagerUsd: 15003.22,
			prizePercentage: 0.9,
			prizeUsd: prizePool * 0.009,
		},
		{
			rank: 8,
			address: '0x11c0...5288',
			displayName: 'starlit',
			wagerUsd: 13320.5,
			prizePercentage: 0.7,
			prizeUsd: prizePool * 0.007,
		},
		{
			rank: 9,
			address: '0x7811...901a',
			displayName: 'wildmint',
			wagerUsd: 12102.1,
			prizePercentage: 0.5,
			prizeUsd: prizePool * 0.005,
		},
		{
			rank: 10,
			address: '0x80ab...7710',
			displayName: 'zenith',
			wagerUsd: 10984.43,
			prizePercentage: 0.4,
			prizeUsd: prizePool * 0.004,
		},
	];

	return {
		snapshot: {
			id: 'weekly-contest-fallback',
			title: 'Weekly Wagering Race',
			prizePoolUsd: prizePool,
			prizeCurrency: 'USD',
			startAt: startOfWeek.toISOString(),
			endAt: endOfWeek.toISOString(),
			updatedAt: now.toISOString(),
			leaderboard,
			lastChampion: {
				rank: 1,
				address: '0x3fe9...a19c',
				displayName: 'velvet-dawn',
				wagerUsd: 51230.56,
				prizeUsd: prizePool * 0.5,
				prizePercentage: 50,
				weekRangeLabel: formatRangeLabel(addDays(startOfWeek, -7), addDays(endOfWeek, -7)),
			},
			user: {
				rank: 27,
				wagerUsd: 3982.34,
				targetRank: 10,
				amountNeededUsd: 10984.43 - 3982.34,
				targetLabel: 'Top 10',
			},
			supportedCoins: ['SUI', 'USDC'],
			prizeDistribution: [
				{ rank: 1, percentage: 50, label: 'Champion' },
				{ rank: 2, percentage: 25, label: 'Runner-up' },
				{ rank: 3, percentage: 12, label: 'Top 3' },
				{ rank: 4, percentage: 6, label: 'Fourth place' },
				{ rank: 5, percentage: 3, label: 'Fifth place' },
				{ rank: 6, percentage: 1.5, label: 'Sixth place' },
				{ rank: 7, percentage: 0.9, label: 'Seventh place' },
				{ rank: 8, percentage: 0.7, label: 'Eighth place' },
				{ rank: 9, percentage: 0.5, label: 'Ninth place' },
				{ rank: 10, percentage: 0.4, label: 'Tenth place' },
			],
		},
		history: [
			{
				id: 'week-1',
				rangeLabel: formatRangeLabel(addDays(startOfWeek, -7), addDays(endOfWeek, -7)),
				prizePoolUsd: 172_420,
				leaderboard: leaderboard.map((entry) => ({
					...entry,
					wagerUsd: entry.wagerUsd * (0.92 + Math.random() * 0.15),
					prizeUsd: entry.prizeUsd ? entry.prizeUsd * (0.92 + Math.random() * 0.15) : entry.prizeUsd,
				})),
			},
			{
				id: 'week-2',
				rangeLabel: formatRangeLabel(addDays(startOfWeek, -14), addDays(endOfWeek, -14)),
				prizePoolUsd: 161_870,
				leaderboard: leaderboard.map((entry) => ({
					...entry,
					wagerUsd: entry.wagerUsd * (0.85 + Math.random() * 0.2),
					prizeUsd: entry.prizeUsd ? entry.prizeUsd * (0.85 + Math.random() * 0.2) : entry.prizeUsd,
				})),
			},
		],
	};
}

function getStartOfWeek(date: Date): Date {
	const start = new Date(date);
	const day = start.getDay();
	const diff = start.getDate() - day + (day === 0 ? -6 : 1); // start week on Monday
	start.setDate(diff);
	start.setHours(0, 0, 0, 0);
	return start;
}

function addDays(date: Date, days: number): Date {
	const result = new Date(date);
	result.setDate(result.getDate() + days);
	return result;
}

function formatRangeLabel(start: Date, end: Date): string {
	const formatter = new Intl.DateTimeFormat('en-US', {
		month: 'short',
		day: 'numeric',
	});

	return `${formatter.format(start)} – ${formatter.format(end)}`;
}

export const contestRepository = new ContestRepository();
